/**
 * Call It In LLC — AxisSync: Create Case
 * Surgical supply logistics form — mirrors the Task Create UI pattern.
 */

import React, { useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { request } from "../../../api/client";
import PlacesAutocompleteInput from "../../../components/PlacesAutocompleteInput";
import DashboardLayout from "../../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../../examples/Navbars/DashboardNavbar";
import MDBox from "../../../components/MDBox";
import MDTypography from "../../../components/MDTypography";
import MDButton from "../../../components/MDButton";
import MDSnackbar from "../../../components/MDSnackbar";
import MDInput from "../../../components/MDInput";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Switch from "@mui/material/Switch";

const DELIVERY_DESTINATIONS = ["to SPD", "to Vendor Room", "to Dock", "to Staff Direct"];

const PROCEDURE_TYPES    = ["Hip", "Knee", "Spine", "Shoulder", "Femur", "Tibia", "Trauma", "Other"];
const LATERALITY_OPTIONS = ["N/A", "Left", "Right", "Bilateral"];
const CASE_TYPES         = ["Primary", "Revision", "Trauma", "STAT"];

const COORDINATION_TYPES = [
  "Standard Delivery",
  "SPD Return",
  "Emergency",
  "Holdover",
];

const WORKFLOW_INTENTS = [
  { value: "pickup-deliver",      label: "Pickup → Deliver" },
  { value: "pickup-hold-deliver", label: "Pickup → Hold → Deliver" },
  { value: "multi-step",          label: "Multi-step (Future)" },
];

const TEMP_CONTROL_OPTIONS = ["Ambient", "Refrigerated", "Frozen"];

// ─── Procedure helpers ────────────────────────────────────────────────────────

/**
 * Build a human-readable procedure summary.
 * Format: "[Laterality] [Type] – [CaseType]"
 */
function procedureSummary(type, laterality, caseType) {
  if (!type) return null;
  const lat = laterality && laterality !== "N/A" ? `${laterality} ` : "";
  return `${lat}${type} – ${caseType}`;
}

/**
 * Encode procedure data as a [PROC] prefix in the notes field.
 * Backward-compatible: existing notes without this prefix are unaffected.
 * Format: "[PROC] Left Hip – Primary | optional proc note\nCoordination notes..."
 */
function buildNotes(form) {
  const { procedureType, laterality, caseType, procedureNotes, notes } = form;
  const parts = [];
  if (procedureType) {
    const summary = procedureSummary(procedureType, laterality, caseType);
    const procLine = procedureNotes.trim()
      ? `[PROC] ${summary} | ${procedureNotes.trim()}`
      : `[PROC] ${summary}`;
    parts.push(procLine);
  }
  if (notes.trim()) parts.push(notes.trim());
  return parts.join("\n");
}

// ─── Case Intelligence helpers ────────────────────────────────────────────────

const RISK_STYLE = {
  low:      { bg: "#DCFCE7", color: "#15803D", label: "Low Risk"      },
  medium:   { bg: "#FEF9C3", color: "#854D0E", label: "Moderate Risk" },
  high:     { bg: "#FED7AA", color: "#C2410C", label: "High Risk"     },
  critical: { bg: "#FEE2E2", color: "#B91C1C", label: "Critical"      },
};

const STATUS_STYLE = {
  "Ready for Dispatch":    { bg: "#DBEAFE", color: "#1D4ED8" },
  "Pending Coordination":  { bg: "#FED7AA", color: "#C2410C" },
  "At Risk":               { bg: "#FED7AA", color: "#C2410C" },
  "Exception":             { bg: "#FEE2E2", color: "#B91C1C" },
  "Pending Review":        { bg: "#F1F5F9", color: "#475569" },
};

const TASK_TYPE_STYLE = {
  pickup:   { bg: "#E0F2FE", color: "#0369A1", label: "PICKUP"   },
  delivery: { bg: "#DCFCE7", color: "#15803D", label: "DELIVERY" },
};

// Backend timestamps use datetime.utcnow().isoformat() — no 'Z' suffix.
// Without appending 'Z', JS treats the string as local time and the displayed
// hour will be off by the UTC offset.  Append 'Z' only when no offset is present.
function _toUTC(iso) {
  if (!iso) return iso;
  const s = String(iso);
  if (s.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(s)) return s;
  return s + "Z";
}

function fmtMilestone(isoStr) {
  if (!isoStr) return "—";
  try {
    const d = new Date(_toUTC(isoStr));
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return isoStr; }
}

function MilestoneCell({ label, value, highlight }) {
  return (
    <MDBox
      sx={{
        p: 1.5,
        borderRadius: "10px",
        background: highlight ? "#FEF9C3" : "#F8FAFC",
        border: `1px solid ${highlight ? "#FDE047" : "#E2E8F0"}`,
        textAlign: "center",
      }}
    >
      <MDTypography variant="caption" fontWeight="bold" color="text"
        sx={{ textTransform: "uppercase", letterSpacing: "0.05em", display: "block", mb: 0.5 }}>
        {label}
      </MDTypography>
      <MDTypography variant="body2" fontWeight="bold" color={highlight ? "warning" : "text"}>
        {value}
      </MDTypography>
    </MDBox>
  );
}

function GeneratedTaskCard({ task }) {
  const typeKey = task.taskType || "delivery";
  const ts = TASK_TYPE_STYLE[typeKey] || TASK_TYPE_STYLE.delivery;
  return (
    <MDBox
      sx={{
        border: "1px solid #E2E8F0",
        borderRadius: "10px",
        p: 2,
        background: "#fff",
      }}
    >
      <MDBox display="flex" alignItems="center" gap={1} mb={1}>
        <MDBox sx={{ background: ts.bg, color: ts.color, fontWeight: 800, fontSize: 10, px: 1.25, py: 0.25, borderRadius: 99, letterSpacing: "0.06em" }}>
          {ts.label}
        </MDBox>
        <MDBox sx={{ background: "#F1F5F9", color: "#475569", fontWeight: 700, fontSize: 10, px: 1.25, py: 0.25, borderRadius: 99 }}>
          Stop {task.sequenceOrder}
        </MDBox>
        {task.podRequirement && task.podRequirement !== "none" && (
          <MDBox sx={{ background: "#FEF9C3", color: "#854D0E", fontWeight: 700, fontSize: 10, px: 1.25, py: 0.25, borderRadius: 99 }}>
            POD: {task.podRequirement === "signature" ? "✍️ Sig" : task.podRequirement}
          </MDBox>
        )}
      </MDBox>
      <MDTypography variant="button" fontWeight="bold" color="text" display="block" mb={0.5}>
        {task.clientName}
      </MDTypography>
      {task.pickupAddress && task.pickupAddress !== task.dropoffAddress && (
        <MDTypography variant="caption" color="text" display="block">
          <strong>From:</strong> {task.pickupAddress}
        </MDTypography>
      )}
      {task.dropoffAddress && (
        <MDTypography variant="caption" color="text" display="block">
          <strong>To:</strong> {task.dropoffAddress}
        </MDTypography>
      )}
      <MDTypography variant="caption" color="secondary" display="block" mt={0.5}
        sx={{ fontFamily: "monospace", fontSize: 10 }}>
        ID: {task.id}
      </MDTypography>
    </MDBox>
  );
}

function CaseIntelligencePanel({ data, onDismiss }) {
  const risk     = RISK_STYLE[data.riskLevel]   || RISK_STYLE.low;
  const status   = STATUS_STYLE[data.initialStatus] || { bg: "#F1F5F9", color: "#475569" };
  const m        = data.milestones || {};
  const tasks    = data.generatedTasks || [];
  const isAtRisk = ["high", "critical"].includes(data.riskLevel);

  return (
    <MDBox mt={4}>
      <Card
        sx={{
          p: 4,
          borderRadius: "1rem",
          border: `2px solid ${isAtRisk ? "#EF4444" : "#10B981"}`,
          boxShadow: isAtRisk
            ? "0 0 0 4px rgba(239,68,68,0.12), 0 8px 32px rgba(0,0,0,0.08)"
            : "0 4px 24px rgba(16,185,129,0.12)",
        }}
      >
        <MDBox display="flex" alignItems="flex-start" justifyContent="space-between" mb={3} gap={2}>
          <MDBox>
            <MDTypography variant="h5" fontWeight={800} color={isAtRisk ? "error" : "text"} mb={0.5}>
              {isAtRisk ? "⚠️" : "✅"} Coordination Intelligence
            </MDTypography>
            <MDTypography variant="caption" color="text">
              Case ID: <strong style={{ fontFamily: "monospace" }}>{data.case_id}</strong>
            </MDTypography>
          </MDBox>
          <MDBox display="flex" gap={1} flexWrap="wrap" alignItems="center">
            <Chip
              label={data.initialStatus || "Pending Coordination"}
              size="small"
              sx={{ background: status.bg, color: status.color, fontWeight: 800, fontSize: 11 }}
            />
            <Chip
              label={risk.label}
              size="small"
              sx={{ background: risk.bg, color: risk.color, fontWeight: 800, fontSize: 11 }}
            />
            <MDBox onClick={onDismiss} sx={{ cursor: "pointer", color: "#9CA3AF", fontSize: 18, lineHeight: 1 }}>
              ✕
            </MDBox>
          </MDBox>
        </MDBox>

        <Divider sx={{ mb: 3 }} />

        <MDBox mb={3}>
          <MDTypography variant="overline" fontWeight="bold" color="text" letterSpacing={1} mb={1.5} display="block">
            Timeline Milestones
          </MDTypography>
          <Grid container spacing={1.5}>
            {[
              { label: "Surgery",           value: fmtMilestone(m.surgeryDateTime) },
              { label: "Delivery Deadline", value: fmtMilestone(m.deliveryDeadline), highlight: isAtRisk },
              { label: "Pickup By",         value: fmtMilestone(m.recommendedPickupTime) },
              { label: "Dispatch By",       value: fmtMilestone(m.recommendedDispatchTime), highlight: isAtRisk },
            ].map(({ label, value, highlight }) => (
              <Grid item xs={6} md={3} key={label}>
                <MilestoneCell label={label} value={value} highlight={!!highlight} />
              </Grid>
            ))}
          </Grid>
        </MDBox>

        <Divider sx={{ mb: 3 }} />

        <MDBox mb={3}>
          <MDTypography variant="overline" fontWeight="bold" color="text" letterSpacing={1} mb={1.5} display="block">
            Generated Tasks ({tasks.length})
          </MDTypography>
          <Grid container spacing={2}>
            {tasks.map((t) => (
              <Grid item xs={12} md={6} key={t.id}>
                <GeneratedTaskCard task={t} />
              </Grid>
            ))}
          </Grid>
        </MDBox>

        <Divider sx={{ mb: 3 }} />

        <MDBox>
          <MDTypography variant="overline" fontWeight="bold" color="text" letterSpacing={1} mb={1.5} display="block">
            Actions Required
          </MDTypography>
          <MDBox component="ul" sx={{ m: 0, pl: 2.5, "& li": { mb: 0.75, fontSize: 13, color: "#374151" } }}>
            <li>Assign a driver to both generated tasks on the <strong>Task List</strong> board.</li>
            <li>Confirm the vendor rep (<strong>{data.generatedTasks?.[0]?.vendorRepPhone || "—"}</strong>) is aware of the pickup window.</li>
            {isAtRisk && (
              <li style={{ color: "#B91C1C", fontWeight: 700 }}>
                ⚠️ Delivery deadline is within the at-risk window — dispatch immediately.
              </li>
            )}
            {data.sms_alert_sent && (
              <li style={{ color: "#15803D", fontWeight: 700 }}>
                ✅ Emergency SMS alert was sent to dispatch.
              </li>
            )}
          </MDBox>
        </MDBox>
      </Card>
    </MDBox>
  );
}

const BLANK_FORM = {
  // Coordination builder — UI only
  coordinationType: "Standard Delivery",
  workflowIntent:   "pickup-deliver",
  // Identity
  caseNumber:       "",
  hospitalName:     "",
  hospitalAddress:  "",
  surgeonName:      "",
  vendorRep:        "",
  vendorRepPhone:   "",
  vendorAddress:    "",
  surgeryDate:      "",
  surgeryTime:      "",
  deliveryDeadline: "",
  // Procedure
  procedureType:    "",
  laterality:       "N/A",
  caseType:         "Primary",
  procedureNotes:   "",
  // Equipment
  trayCount:           "",
  trayDestination:     "to SPD",
  implantDestination:  "to SPD",
  implantTotesSmall:   "",
  implantTotesMedium:  "",
  implantTotesLarge:   "",
  // Delivery requirements — UI only (not yet wired to backend)
  requiresSignature: false,
  tempControl:       "Ambient",
  chainOfCustody:    false,
  // Coordination
  notes:        "",
  isEmergency:  false,
};

const SectionBar = ({ color }) => (
  <MDBox sx={{ width: 4, height: 20, bgcolor: `${color}.main`, borderRadius: 1 }} />
);

const selectStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: "8px",
  border: "1px solid #d2d6da",
  outline: "none",
  color: "#495057",
  fontSize: "0.875rem",
  background: "#fff",
  cursor: "pointer",
};

const FieldLabel = ({ children }) => (
  <MDTypography component="label" variant="button" fontWeight="regular" color="text" display="block" mb={0.5}>
    {children}
  </MDTypography>
);

export default function CaseCreate() {
  const [form, setForm] = useState(BLANK_FORM);
  const [saving, setSaving] = useState(false);

  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadedUrl, setUploadedUrl] = useState("");
  const fileInputRef = useRef(null);

  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [savedCase, setSavedCase] = useState(null);

  const closeSuccessSB = () => setSuccessSB(false);
  const closeErrorSB   = () => setErrorSB(false);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  function handleFileChange(e) {
    const file = e.target.files[0] || null;
    setSelectedFile(file);
    setUploadedUrl("");
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (saving) return;

    if (!form.hospitalName.trim()) {
      setErrorMessage("Receiving facility name is required.");
      setErrorSB(true);
      return;
    }
    if (!form.surgeryDate.trim()) {
      setErrorMessage("Surgery date is required.");
      setErrorSB(true);
      return;
    }

    setSaving(true);

    try {
      // Step 1: Upload attachment if a file was selected
      let attachmentUrl = uploadedUrl;
      if (selectedFile && !uploadedUrl) {
        const formData = new FormData();
        formData.append("file", selectedFile);
        const uploadRes = await request("/upload/", {
          method: "POST",
          rawBody: formData,
        });
        if (!uploadRes.ok) {
          const err = await uploadRes.json().catch(() => ({}));
          throw new Error(err?.detail ?? `Upload failed (HTTP ${uploadRes.status})`);
        }
        const uploadData = await uploadRes.json();
        attachmentUrl = uploadData.url;
        setUploadedUrl(attachmentUrl);
      }

      // Step 2: POST the case
      // Procedure is encoded two ways:
      //   a) As a structured `procedure` object (forward-compatible; stored if backend extends CaseIn)
      //   b) As a [PROC] prefix in the `notes` string (guaranteed persistence against current schema)
      const payload = {
        id:               uuidv4(),
        caseNumber:       form.caseNumber.trim(),
        hospitalName:     form.hospitalName.trim(),
        hospitalAddress:  form.hospitalAddress.trim(),
        surgeonName:      form.surgeonName.trim(),
        vendorRep:        form.vendorRep.trim(),
        vendorRepPhone:   form.vendorRepPhone.trim(),
        vendorAddress:    form.vendorAddress.trim(),
        surgeryDate:      form.surgeryDate,
        surgeryTime:      form.surgeryTime,
        deliveryDeadline: form.deliveryDeadline,
        trayCount:        form.trayCount === "" ? 0 : Number(form.trayCount),
        trayDestination:  form.trayDestination,
        implantDestination:  form.implantDestination,
        implantTotesSmall:   form.implantTotesSmall   === "" ? 0 : Number(form.implantTotesSmall),
        implantTotesMedium:  form.implantTotesMedium  === "" ? 0 : Number(form.implantTotesMedium),
        implantTotesLarge:   form.implantTotesLarge   === "" ? 0 : Number(form.implantTotesLarge),
        // Combined notes: [PROC] prefix + coordination notes
        notes:            buildNotes(form),
        status:           "Pending Coordination",
        billingTotal:     0,
        attachmentUrl,
        isEmergency:      form.isEmergency,
        // Origin metadata — identifies this as a courier-layer operator-assisted intake.
        // These values distinguish cases created here from future vendor-portal or
        // hospital-module intakes.  Mirrors the backend CaseIn defaults for explicitness.
        originLayer:  "courier",
        originModule: "callitin-ops",
        createdVia:   "operator-assisted-intake",
        // Structured procedure object — stored if backend accepts it; otherwise gracefully ignored
        procedure: form.procedureType ? {
          type:      form.procedureType,
          laterality: form.laterality,
          caseType:  form.caseType,
          notes:     form.procedureNotes.trim(),
        } : null,
      };

      const res = await request("/cases/", {
        method: "POST",
        body: payload,
      });

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody?.detail || `Server responded with ${res.status}`);
      }

      const caseResult = await res.json();
      setSavedCase(caseResult);
      setSuccessSB(true);
      setForm(BLANK_FORM);
      setSelectedFile(null);
      setUploadedUrl("");
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      console.error("AxisSync submit error:", err);
      setErrorMessage(err.message || "Failed to connect to backend. Check that the server is running.");
      setErrorSB(true);
    } finally {
      setSaving(false);
    }
  }

  const liveSummary = procedureSummary(form.procedureType, form.laterality, form.caseType);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={4} px={{ xs: 2, md: 3 }} sx={{ overflowX: "hidden" }}>

        <MDBox sx={{ maxWidth: "960px", mx: "auto" }}>
          <form onSubmit={handleSubmit}>

            {/* Page header */}
            <MDBox mb={4}>
              <MDTypography variant="h3" fontWeight="bold">
                Create Coordination
              </MDTypography>
              <MDTypography variant="button" color="text">
                Operator-assisted intake — builds an AxisSync coordination and
                routes it for courier dispatch by Call It In.
              </MDTypography>
            </MDBox>

            {/* ── Card 1: Coordination Type ──────────────────────────────────── */}
            <Card sx={{ p: 4, mb: 4, borderRadius: "1rem", boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)" }}>
              <MDTypography variant="h6" fontWeight={800} mb={3} display="flex" alignItems="center" gap={1} sx={{ letterSpacing: "-0.01em" }}>
                <SectionBar color="info" />
                Coordination Type
              </MDTypography>
              <Grid container spacing={2}>
                {COORDINATION_TYPES.map((type) => {
                  const isSelected = form.coordinationType === type;
                  const isEmergencyType = type === "Emergency";
                  return (
                    <Grid item xs={6} md={3} key={type}>
                      <MDBox
                        onClick={() => setForm((f) => ({
                          ...f,
                          coordinationType: type,
                          isEmergency: isEmergencyType ? true : f.isEmergency,
                        }))}
                        sx={{
                          p: 2,
                          borderRadius: "10px",
                          border: "2px solid",
                          borderColor: isSelected
                            ? (isEmergencyType ? "#EF4444" : "#3b82f6")
                            : "#E2E8F0",
                          background: isSelected
                            ? (isEmergencyType ? "rgba(239,68,68,0.06)" : "rgba(59,130,246,0.06)")
                            : "#fff",
                          cursor: "pointer",
                          textAlign: "center",
                          transition: "all 0.15s ease",
                          userSelect: "none",
                        }}
                      >
                        <MDTypography
                          variant="button"
                          fontWeight={isSelected ? 800 : 500}
                          color={isSelected ? (isEmergencyType ? "error" : "info") : "text"}
                          display="block"
                        >
                          {isEmergencyType && isSelected ? "🚨 " : ""}{type}
                        </MDTypography>
                      </MDBox>
                    </Grid>
                  );
                })}
              </Grid>
            </Card>

            {/* ── Card 2: Workflow Intent ────────────────────────────────────── */}
            <Card sx={{ p: 4, mb: 4, borderRadius: "1rem", boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)" }}>
              <MDTypography variant="h6" fontWeight={800} mb={3} display="flex" alignItems="center" gap={1} sx={{ letterSpacing: "-0.01em" }}>
                <SectionBar color="info" />
                Workflow Intent
              </MDTypography>
              <Grid container spacing={2}>
                {WORKFLOW_INTENTS.map(({ value, label }) => {
                  const isSelected = form.workflowIntent === value;
                  const isFuture = value === "multi-step";
                  return (
                    <Grid item xs={12} md={4} key={value}>
                      <MDBox
                        onClick={() => !isFuture && setForm((f) => ({ ...f, workflowIntent: value }))}
                        sx={{
                          p: 2.5,
                          borderRadius: "10px",
                          border: "2px solid",
                          borderColor: isSelected ? "#f97316" : "#E2E8F0",
                          background: isSelected ? "rgba(249,115,22,0.06)" : "#fff",
                          cursor: isFuture ? "not-allowed" : "pointer",
                          opacity: isFuture ? 0.45 : 1,
                          transition: "all 0.15s ease",
                          userSelect: "none",
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                        }}
                      >
                        <MDBox
                          sx={{
                            width: 10, height: 10, borderRadius: "50%",
                            background: isSelected ? "#f97316" : "#CBD5E1",
                            flexShrink: 0,
                            transition: "background 0.15s ease",
                          }}
                        />
                        <MDBox>
                          <MDTypography
                            variant="button"
                            fontWeight={isSelected ? 800 : 500}
                            color={isSelected ? "text" : "text"}
                            display="block"
                          >
                            {label}
                          </MDTypography>
                          {isFuture && (
                            <MDTypography variant="caption" color="text" sx={{ opacity: 0.6 }}>
                              Available in a future release
                            </MDTypography>
                          )}
                        </MDBox>
                      </MDBox>
                    </Grid>
                  );
                })}
              </Grid>
            </Card>

            {/* ── Card 3: Coordination Identity ─────────────────────────────── */}
            <Card sx={{ p: 4, mb: 4, borderRadius: "1rem", boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)" }}>
              <MDTypography variant="h6" fontWeight={800} mb={3} display="flex" alignItems="center" gap={1} sx={{ letterSpacing: "-0.01em" }}>
                <SectionBar color="info" />
                Coordination Identity
              </MDTypography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <MDInput
                    fullWidth
                    label="Coordination / PO Number"
                    value={form.caseNumber}
                    onChange={set("caseNumber")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <MDInput
                    fullWidth
                    label="Surgeon"
                    value={form.surgeonName}
                    onChange={set("surgeonName")}
                  />
                </Grid>
              </Grid>
            </Card>

            {/* ── Card 4: Procedure ──────────────────────────────────────────── */}
            <Card sx={{ p: 4, mb: 4, borderRadius: "1rem", boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)" }}>
              <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={3} gap={2} flexWrap="wrap">
                <MDTypography variant="h6" fontWeight={800} display="flex" alignItems="center" gap={1} sx={{ letterSpacing: "-0.01em" }}>
                  <SectionBar color="warning" />
                  Procedure
                </MDTypography>
                {/* Live summary badge */}
                {liveSummary && (
                  <MDBox
                    sx={{
                      background: "#FEF9C3",
                      border: "1px solid #FDE047",
                      borderRadius: "8px",
                      px: 2,
                      py: 0.75,
                    }}
                  >
                    <MDTypography variant="button" fontWeight={800} color="text">
                      {liveSummary}
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>

              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <FieldLabel>Procedure Type</FieldLabel>
                  <select style={selectStyle} value={form.procedureType} onChange={set("procedureType")}>
                    <option value="">Select…</option>
                    {PROCEDURE_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FieldLabel>Laterality</FieldLabel>
                  <select style={selectStyle} value={form.laterality} onChange={set("laterality")}>
                    {LATERALITY_OPTIONS.map((l) => (
                      <option key={l} value={l}>{l}</option>
                    ))}
                  </select>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FieldLabel>Case Type</FieldLabel>
                  <select style={selectStyle} value={form.caseType} onChange={set("caseType")}>
                    {CASE_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </Grid>
                <Grid item xs={12}>
                  <MDInput
                    fullWidth
                    label="Procedure Notes (optional)"
                    value={form.procedureNotes}
                    onChange={set("procedureNotes")}
                    multiline
                    rows={2}
                  />
                </Grid>
              </Grid>
            </Card>

            {/* ── Card 5: Origin Details ────────────────────────────────────── */}
            <Card sx={{ p: 4, mb: 4, borderRadius: "1rem", boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)" }}>
              <MDTypography variant="h6" fontWeight={800} mb={3} display="flex" alignItems="center" gap={1} sx={{ letterSpacing: "-0.01em" }}>
                <SectionBar color="info" />
                Origin Details
              </MDTypography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <PlacesAutocompleteInput
                    label="Vendor Pickup Address"
                    placeholder="e.g. 5678 Warehouse Blvd, Aurora, CO 80011"
                    value={form.vendorAddress}
                    onChange={(addr) => setForm((f) => ({ ...f, vendorAddress: addr }))}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <MDInput
                    fullWidth
                    label="Vendor Rep Name"
                    value={form.vendorRep}
                    onChange={set("vendorRep")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <MDInput
                    fullWidth
                    label="Vendor Rep Phone"
                    value={form.vendorRepPhone}
                    onChange={set("vendorRepPhone")}
                    inputProps={{ type: "tel" }}
                  />
                </Grid>
              </Grid>
            </Card>

            {/* ── Card 6: Destination Details ───────────────────────────────── */}
            <Card sx={{ p: 4, mb: 4, borderRadius: "1rem", boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)" }}>
              <MDTypography variant="h6" fontWeight={800} mb={3} display="flex" alignItems="center" gap={1} sx={{ letterSpacing: "-0.01em" }}>
                <SectionBar color="info" />
                Destination Details
              </MDTypography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <MDInput
                    fullWidth required
                    label="Hospital / Receiving Facility *"
                    value={form.hospitalName}
                    onChange={set("hospitalName")}
                  />
                </Grid>
                <Grid item xs={12}>
                  <PlacesAutocompleteInput
                    label="Hospital Delivery Address"
                    placeholder="e.g. 1234 Medical Center Dr, Denver, CO 80203"
                    value={form.hospitalAddress}
                    onChange={(addr) => setForm((f) => ({ ...f, hospitalAddress: addr }))}
                  />
                </Grid>
              </Grid>
            </Card>

            {/* ── Card 7: Timing / Urgency ──────────────────────────────────── */}
            <Card
              sx={{
                p: 4, mb: 4, borderRadius: "1rem",
                boxShadow: form.isEmergency
                  ? "0 0 0 3px #EF4444, 0 8px 30px rgba(239,68,68,0.18)"
                  : "0 4px 20px 0 rgba(0,0,0,0.05)",
                transition: "box-shadow 0.2s ease",
              }}
            >
              <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={2}>
                <MDTypography variant="h6" fontWeight={800} display="flex" alignItems="center" gap={1} sx={{ letterSpacing: "-0.01em" }}>
                  <SectionBar color={form.isEmergency ? "error" : "warning"} />
                  Timing / Urgency
                </MDTypography>
                {/* Trauma / STAT toggle */}
                <MDBox
                  display="flex" alignItems="center" gap={1} px={2} py={1} borderRadius="lg"
                  sx={{
                    border: "1px solid",
                    borderColor: form.isEmergency ? "error.main" : "#e0e0e0",
                    background: form.isEmergency ? "rgba(239,68,68,0.06)" : "transparent",
                    transition: "all 0.2s ease",
                  }}
                >
                  <Switch
                    checked={form.isEmergency}
                    onChange={(e) => setForm((f) => ({ ...f, isEmergency: e.target.checked }))}
                    color="error"
                    size="small"
                  />
                  <MDBox>
                    <MDTypography variant="button" fontWeight="bold" color={form.isEmergency ? "error" : "text"}>
                      {form.isEmergency ? "🚨 TRAUMA / STAT EMERGENCY" : "Trauma / STAT Emergency"}
                    </MDTypography>
                    {form.isEmergency && (
                      <MDTypography variant="caption" color="error" display="block" fontWeight="bold">
                        Warning: This will trigger an immediate emergency dispatch.
                      </MDTypography>
                    )}
                  </MDBox>
                </MDBox>
              </MDBox>

              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <MDTypography component="label" variant="button" fontWeight="regular" color="text" display="block" mb={0.5}>
                    Surgery Date *
                  </MDTypography>
                  <MDInput fullWidth required inputProps={{ type: "date" }} value={form.surgeryDate} onChange={set("surgeryDate")} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <MDTypography component="label" variant="button" fontWeight="regular" color="text" display="block" mb={0.5}>
                    Surgery Time
                  </MDTypography>
                  <MDInput fullWidth inputProps={{ type: "time" }} value={form.surgeryTime} onChange={set("surgeryTime")} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <MDTypography component="label" variant="button" fontWeight="regular" color="text" display="block" mb={0.5}>
                    Delivery Deadline
                  </MDTypography>
                  <MDInput fullWidth inputProps={{ type: "datetime-local" }} value={form.deliveryDeadline} onChange={set("deliveryDeadline")} />
                </Grid>
              </Grid>
            </Card>

            {/* ── Delivery Requirements ─────────────────────────────────────── */}
            <Card sx={{ p: 4, mb: 4, borderRadius: "1rem", boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)" }}>
              <MDTypography variant="h6" fontWeight={800} mb={3} display="flex" alignItems="center" gap={1} sx={{ letterSpacing: "-0.01em" }}>
                <SectionBar color="warning" />
                Delivery Requirements
              </MDTypography>
              <Grid container spacing={3} alignItems="flex-start">

                {/* Requires Signature */}
                <Grid item xs={12} md={4}>
                  <MDBox
                    onClick={() => setForm((f) => ({ ...f, requiresSignature: !f.requiresSignature }))}
                    display="flex" alignItems="center" gap={1.5}
                    sx={{
                      p: 2, borderRadius: "10px", border: "2px solid",
                      borderColor: form.requiresSignature ? "#f97316" : "#E2E8F0",
                      background: form.requiresSignature ? "rgba(249,115,22,0.06)" : "#fff",
                      cursor: "pointer", userSelect: "none", transition: "all 0.15s ease",
                    }}
                  >
                    <MDBox
                      sx={{
                        width: 20, height: 20, borderRadius: "4px", border: "2px solid",
                        borderColor: form.requiresSignature ? "#f97316" : "#CBD5E1",
                        background: form.requiresSignature ? "#f97316" : "#fff",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0, transition: "all 0.15s ease",
                      }}
                    >
                      {form.requiresSignature && (
                        <Icon sx={{ fontSize: "14px !important", color: "#fff" }}>check</Icon>
                      )}
                    </MDBox>
                    <MDBox>
                      <MDTypography variant="button" fontWeight={form.requiresSignature ? 800 : 500} color="text" display="block">
                        Requires Signature
                      </MDTypography>
                      <MDTypography variant="caption" color="text" sx={{ opacity: 0.65 }}>
                        POD signature on delivery
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </Grid>

                {/* Chain-of-Custody Required */}
                <Grid item xs={12} md={4}>
                  <MDBox
                    onClick={() => setForm((f) => ({ ...f, chainOfCustody: !f.chainOfCustody }))}
                    display="flex" alignItems="center" gap={1.5}
                    sx={{
                      p: 2, borderRadius: "10px", border: "2px solid",
                      borderColor: form.chainOfCustody ? "#4ade80" : "#E2E8F0",
                      background: form.chainOfCustody ? "rgba(74,222,128,0.06)" : "#fff",
                      cursor: "pointer", userSelect: "none", transition: "all 0.15s ease",
                    }}
                  >
                    <MDBox
                      sx={{
                        width: 20, height: 20, borderRadius: "4px", border: "2px solid",
                        borderColor: form.chainOfCustody ? "#4ade80" : "#CBD5E1",
                        background: form.chainOfCustody ? "#4ade80" : "#fff",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0, transition: "all 0.15s ease",
                      }}
                    >
                      {form.chainOfCustody && (
                        <Icon sx={{ fontSize: "14px !important", color: "#fff" }}>check</Icon>
                      )}
                    </MDBox>
                    <MDBox>
                      <MDTypography variant="button" fontWeight={form.chainOfCustody ? 800 : 500} color="text" display="block">
                        Chain-of-Custody Required
                      </MDTypography>
                      <MDTypography variant="caption" color="text" sx={{ opacity: 0.65 }}>
                        Full handoff documentation
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </Grid>

                {/* Temperature Control */}
                <Grid item xs={12} md={4}>
                  <FieldLabel>Temperature Control</FieldLabel>
                  <select
                    style={{
                      ...selectStyle,
                      borderColor: form.tempControl !== "Ambient" ? "#3b82f6" : "#d2d6da",
                      background: form.tempControl !== "Ambient" ? "rgba(59,130,246,0.04)" : "#fff",
                    }}
                    value={form.tempControl}
                    onChange={set("tempControl")}
                  >
                    {TEMP_CONTROL_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                  {form.tempControl !== "Ambient" && (
                    <MDTypography variant="caption" color="info" display="block" mt={0.5} fontWeight="bold">
                      Cold-chain handling required
                    </MDTypography>
                  )}
                </Grid>

              </Grid>
            </Card>

            {/* ── Card 5: Load / Handling ────────────────────────────────────── */}
            <Card sx={{ p: 4, mb: 4, borderRadius: "1rem", boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)" }}>
              <MDTypography variant="h6" fontWeight={800} mb={3} display="flex" alignItems="center" gap={1} sx={{ letterSpacing: "-0.01em" }}>
                <SectionBar color="success" />
                Load / Handling
              </MDTypography>

              <MDBox mb={1} pb={2} sx={{ borderBottom: "1px solid #f0f0f0" }}>
                <MDTypography variant="overline" fontWeight="bold" color="text" letterSpacing={1}>
                  Surgical Trays
                </MDTypography>
              </MDBox>
              <Grid container spacing={3} mb={4}>
                <Grid item xs={12} md={3}>
                  <MDInput
                    fullWidth type="number" label="Tray Count"
                    value={form.trayCount} onChange={set("trayCount")} inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FieldLabel>Tray Delivery Destination</FieldLabel>
                  <select style={selectStyle} value={form.trayDestination} onChange={set("trayDestination")}>
                    {DELIVERY_DESTINATIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </Grid>
              </Grid>

              <MDBox mb={1} pb={2} sx={{ borderBottom: "1px solid #f0f0f0" }}>
                <MDTypography variant="overline" fontWeight="bold" color="text" letterSpacing={1}>
                  Implants &amp; Biologics
                </MDTypography>
              </MDBox>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <FieldLabel>Implant Delivery Destination</FieldLabel>
                  <select style={selectStyle} value={form.implantDestination} onChange={set("implantDestination")}>
                    {DELIVERY_DESTINATIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </Grid>
                <Grid item xs={12}>
                  <MDTypography variant="caption" fontWeight="bold" color="text" display="block" mb={2}>
                    Tote Quantities
                  </MDTypography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <MDInput fullWidth type="number" label="Small Totes" value={form.implantTotesSmall} onChange={set("implantTotesSmall")} inputProps={{ min: 0 }} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <MDInput fullWidth type="number" label="Medium Totes" value={form.implantTotesMedium} onChange={set("implantTotesMedium")} inputProps={{ min: 0 }} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <MDInput fullWidth type="number" label="Large Totes" value={form.implantTotesLarge} onChange={set("implantTotesLarge")} inputProps={{ min: 0 }} />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Card>

            {/* ── Card 6: Coordination Instructions ─────────────────────────── */}
            <Card sx={{ p: 4, mb: 4, borderRadius: "1rem", boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)" }}>
              <MDTypography variant="h6" fontWeight={800} mb={3} display="flex" alignItems="center" gap={1} sx={{ letterSpacing: "-0.01em" }}>
                <SectionBar color="secondary" />
                Coordination Instructions
              </MDTypography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <MDInput
                    fullWidth
                    label="Instructions (SPD access codes, cold-chain requirements, handling notes…)"
                    value={form.notes}
                    onChange={set("notes")}
                    multiline
                    rows={4}
                  />
                </Grid>
              </Grid>
            </Card>

            {/* ── Card 7: Attachments ────────────────────────────────────────── */}
            <Card sx={{ p: 4, mb: 4, borderRadius: "1rem", boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)" }}>
              <MDTypography variant="h6" fontWeight={800} mb={3} display="flex" alignItems="center" gap={1} sx={{ letterSpacing: "-0.01em" }}>
                <SectionBar color="secondary" />
                Attachments
              </MDTypography>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={8}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                  />
                  <MDBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
                    <MDButton
                      variant="outlined" color="secondary"
                      onClick={() => fileInputRef.current && fileInputRef.current.click()}
                      startIcon={<Icon>attach_file</Icon>}
                    >
                      Choose File
                    </MDButton>
                    <MDTypography variant="button" color="text">
                      {selectedFile ? selectedFile.name : "No file chosen"}
                    </MDTypography>
                  </MDBox>
                  <MDTypography variant="caption" color="text" mt={1} display="block">
                    Accepted: images, PDF, Word, Excel. Max recommended: 10 MB.
                  </MDTypography>
                </Grid>
                {(uploadedUrl || selectedFile) && (
                  <Grid item xs={12} md={4}>
                    <MDBox p={2} borderRadius="lg" sx={{ border: "1px solid #e0e0e0", background: "#fafafa" }}>
                      {uploadedUrl ? (
                        <>
                          <MDTypography variant="caption" color="success" fontWeight="bold" display="block">
                            Uploaded successfully
                          </MDTypography>
                          <MDTypography variant="caption" color="text" sx={{ wordBreak: "break-all" }}>
                            {uploadedUrl}
                          </MDTypography>
                        </>
                      ) : (
                        <MDTypography variant="caption" color="text">
                          Ready to upload: <strong>{selectedFile?.name}</strong>
                        </MDTypography>
                      )}
                    </MDBox>
                  </Grid>
                )}
              </Grid>
            </Card>

            {/* ── Save row ───────────────────────────────────────────────────── */}
            <Card
              sx={{
                p: 3, mb: 4, borderRadius: "1rem",
                boxShadow: form.isEmergency
                  ? "0 0 0 3px #EF4444, 0 8px 30px rgba(239,68,68,0.18)"
                  : "0 4px 20px 0 rgba(0,0,0,0.05)",
                transition: "box-shadow 0.2s ease",
                background: form.isEmergency ? "rgba(239,68,68,0.03)" : "#fff",
              }}
            >
              <MDBox display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                <MDBox>
                  <MDTypography
                    variant="overline" fontWeight="bold"
                    color={form.isEmergency ? "error" : "text"}
                    sx={{ opacity: 0.7, letterSpacing: "0.08em" }}
                  >
                    {form.isEmergency ? "🚨 EMERGENCY DISPATCH" : "AXISSYNC COORDINATION BUILDER"}
                  </MDTypography>
                  <MDTypography variant="h6" fontWeight={700} color={form.isEmergency ? "error" : "text"} sx={{ lineHeight: 1.3 }}>
                    {form.hospitalName.trim() || (
                      <span style={{ opacity: 0.4, fontWeight: 400, fontSize: "0.9em" }}>No facility selected</span>
                    )}
                  </MDTypography>
                  {liveSummary && (
                    <MDTypography variant="caption" color="text" sx={{ opacity: 0.65 }}>
                      {liveSummary}
                    </MDTypography>
                  )}
                </MDBox>
                <MDButton
                  variant="gradient"
                  color={form.isEmergency ? "error" : "info"}
                  type="submit"
                  disabled={saving}
                  size="large"
                  sx={{ minWidth: 200, py: 1.5, fontWeight: 800, fontSize: "0.95rem", letterSpacing: "0.03em" }}
                >
                  {saving
                    ? "Initializing…"
                    : form.isEmergency
                    ? "🚨 Emergency Dispatch"
                    : "Initialize Coordination"}
                </MDButton>
              </MDBox>
            </Card>

          </form>

          {/* ── Coordination Intelligence Panel ─────────────────────────────── */}
          {savedCase && <CaseIntelligencePanel data={savedCase} onDismiss={() => setSavedCase(null)} />}

        </MDBox>

        {/* ── Snackbars ───────────────────────────────────────────────────────── */}
        <MDSnackbar
          color="success" icon="check"
          title="Coordination Initialized"
          content="Successfully synced to the AxisSync network."
          dateTime={new Date().toLocaleString()}
          open={successSB} onClose={closeSuccessSB} close={closeSuccessSB} bgWhite
        />
        <MDSnackbar
          color="error" icon="warning"
          title="Coordination Failed"
          content={errorMessage}
          dateTime={new Date().toLocaleString()}
          open={errorSB} onClose={closeErrorSB} close={closeErrorSB} bgWhite
        />
      </MDBox>
    </DashboardLayout>
  );
}
